import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { EMZI_COLORS, EMZI_SHADOWS, EMZI_RADIUS, EMZI_SPACING } from '../styles/emziCI';
import { secureStorage } from '../utils/secureStorage';
import { BACKEND_URL } from '../utils/backendUrl';

const API_BASE = BACKEND_URL;


/**
 * Campaign Direction Manager
 * Mobile-first UI for managing strategic campaign directions (Why Trust/Why Buy)
 * Role: Management/Admin only
 */
const CampaignDirectionManager = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  // State
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [lang, setLang] = useState('ms'); // Language toggle for bilingual support
  // Auto-save draft state
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [lastAutoSavedAt, setLastAutoSavedAt] = useState(null);
  const [autoSavedDraftId, setAutoSavedDraftId] = useState(null); // tracks the draft we keep updating
  // Ref mirror so the 10s setInterval closure always reads the latest id
  // (React state alone produces a stale-closure: tick 2+ would still see null).
  const autoSavedDraftIdRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    campaign_name_ms: '',
    campaign_name_en: '',
    objective: 'sales',
    why_trust: [{ reason_ms: '', reason_en: '', research_source: '', priority: 1 }],
    why_buy: [{ reason_ms: '', reason_en: '', research_source: '', priority: 1 }],
    customer_pain_insights: [{ ms: '', en: '' }],
    solution_positioning_ms: '',
    solution_positioning_en: '',
    key_message_ms: '',
    key_message_en: '',
    applicable_products: [],
    priority_level: 1,
    active_from: new Date().toISOString().split('T')[0],
    active_until: '',
    tone: 'Professional'
  });

  useEffect(() => {
    // Check for action parameter (e.g., ?action=create)
    const action = searchParams.get('action');
    if (action === 'create') {
      handleCreate();
    }
    
    loadCampaigns();
    loadProducts();
  }, [searchParams]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = secureStorage.getItem('nexus_access_token');
      const response = await axios.get(`${API_BASE}/api/v3/packs?pack_type=direction`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaigns(response.data.packs || []);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      setError('Failed to load campaigns. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const token = secureStorage.getItem('nexus_access_token');
      const response = await axios.get(`${API_BASE}/api/v3/packs?pack_type=product&status=published`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data.packs || []);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const handleCreate = () => {
    setShowForm(true);
    setEditingCampaign(null);
    setError(null);
    setSuccess(null);
    setFormData({
      campaign_name_ms: '',
      campaign_name_en: '',
      objective: 'sales',
      why_trust: [{ reason_ms: '', reason_en: '', research_source: '', priority: 1 }],
      why_buy: [{ reason_ms: '', reason_en: '', research_source: '', priority: 1 }],
      customer_pain_insights: [{ ms: '', en: '' }],
      solution_positioning_ms: '',
      solution_positioning_en: '',
      key_message_ms: '',
      key_message_en: '',
      applicable_products: [],
      priority_level: 1,
      active_from: new Date().toISOString().split('T')[0],
      active_until: '',
      tone: 'Professional'
    });
  };

  const handleAddTrustReason = () => {
    setFormData({
      ...formData,
      why_trust: [...formData.why_trust, { reason_ms: '', reason_en: '', research_source: '', priority: formData.why_trust.length + 1 }]
    });
  };

  const handleRemoveTrustReason = (index) => {
    const newTrust = formData.why_trust.filter((_, i) => i !== index);
    setFormData({ ...formData, why_trust: newTrust });
  };

  const handleTrustReasonChange = (index, field, value) => {
    const newTrust = [...formData.why_trust];
    newTrust[index][field] = value;
    setFormData({ ...formData, why_trust: newTrust });
  };

  const handleAddBuyReason = () => {
    setFormData({
      ...formData,
      why_buy: [...formData.why_buy, { reason_ms: '', reason_en: '', research_source: '', priority: formData.why_buy.length + 1 }]
    });
  };

  const handleRemoveBuyReason = (index) => {
    const newBuy = formData.why_buy.filter((_, i) => i !== index);
    setFormData({ ...formData, why_buy: newBuy });
  };

  const handleBuyReasonChange = (index, field, value) => {
    const newBuy = [...formData.why_buy];
    newBuy[index][field] = value;
    setFormData({ ...formData, why_buy: newBuy });
  };

  const handleAddPainInsight = () => {
    setFormData({
      ...formData,
      customer_pain_insights: [...formData.customer_pain_insights, { ms: '', en: '' }]
    });
  };

  const handleRemovePainInsight = (index) => {
    const newPain = formData.customer_pain_insights.filter((_, i) => i !== index);
    setFormData({ ...formData, customer_pain_insights: newPain });
  };

  const handlePainInsightChange = (index, field, value) => {
    const newPain = [...formData.customer_pain_insights];
    newPain[index][field] = value;
    setFormData({ ...formData, customer_pain_insights: newPain });
  };

  const handleProductToggle = (productId) => {
    const newProducts = formData.applicable_products.includes(productId)
      ? formData.applicable_products.filter(id => id !== productId)
      : [...formData.applicable_products, productId];
    setFormData({ ...formData, applicable_products: newProducts });
  };

  const validateForm = () => {
    if (!formData.campaign_name_ms.trim() || !formData.campaign_name_en.trim()) {
      setError('Campaign name (both BM and EN) is required');
      return false;
    }

    if (formData.why_trust.length === 0 || !formData.why_trust[0].reason_ms.trim()) {
      setError('At least one "Why Trust" reason is required');
      return false;
    }

    if (formData.why_buy.length === 0 || !formData.why_buy[0].reason_ms.trim()) {
      setError('At least one "Why Buy" trigger is required');
      return false;
    }

    if (formData.applicable_products.length === 0) {
      setError('Please select at least one product to apply this campaign direction');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e, saveAsDraft = false) => {
    if (e && e.preventDefault) e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setLoading(true);

    try {
      const token = secureStorage.getItem('nexus_access_token');
      const userIdentifier = user?.email || user?.user_id || 'management';

      const desiredStatus = saveAsDraft ? 'draft' : 'published';

      // Build campaign pack structure (matches backend CreatePackV3Request)
      const campaignPack = {
        pack_type: 'direction',
        // created_by / updated_by must be at ROOT — backend validation requires it
        created_by: userIdentifier,
        // Backend stores status inside metadata.status. Pass via metadata_overrides
        // so create_pack() in knowledge_registry_service.py picks it up correctly.
        metadata_overrides: {
          status: desiredStatus,
          visibility: 'public',
          tags: ['campaign-direction', 'strategic'],
        },
        name: {
          ms: formData.campaign_name_ms,
          en: formData.campaign_name_en
        },
        description: {
          ms: formData.solution_positioning_ms || 'Strategic campaign direction',
          en: formData.solution_positioning_en || 'Strategic campaign direction'
        },
        version: editingCampaign ? incrementVersion(editingCampaign.version) : '1.0.0',
        content: {
          campaign_name: {
            ms: formData.campaign_name_ms,
            en: formData.campaign_name_en
          },
          direction_level: 'product',
          objective: formData.objective,
          why_trust: formData.why_trust.map(t => ({
            reason: { ms: t.reason_ms, en: t.reason_en },
            research_source: t.research_source,
            priority: t.priority
          })),
          why_buy: formData.why_buy.map(b => ({
            reason: { ms: b.reason_ms, en: b.reason_en },
            research_source: b.research_source,
            priority: b.priority
          })),
          customer_pain_insights: formData.customer_pain_insights.filter(p => p.ms || p.en),
          solution_positioning: {
            ms: formData.solution_positioning_ms,
            en: formData.solution_positioning_en
          },
          key_message: {
            ms: formData.key_message_ms,
            en: formData.key_message_en
          },
          applicable_products: formData.applicable_products,
          priority_level: formData.priority_level,
          tone: formData.tone
        },
        effective_from: formData.active_from,
        effective_until: formData.active_until || null,
        last_updated: new Date().toISOString(),
        metadata: {
          visibility: 'public',
          tags: ['campaign-direction', 'strategic'],
          created_by: userIdentifier
        }
      };

      if (editingCampaign) {
        // Update existing — backend requires updated_by at ROOT
        campaignPack.pack_id = editingCampaign.pack_id;
        campaignPack.supersedes_version = editingCampaign.version;
        campaignPack.update_reason = 'Updated strategic direction based on research';
        campaignPack.updated_by = userIdentifier;
        campaignPack.change_summary = saveAsDraft
          ? 'Saved as draft'
          : 'Updated and published';

        await axios.put(`${API_BASE}/api/v3/packs/${editingCampaign.pack_id}`, campaignPack, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess(saveAsDraft
          ? '💾 Campaign Direction saved as draft!'
          : '✅ Campaign Direction updated and published!');
      } else if (autoSavedDraftIdRef.current) {
        // We have an auto-saved draft — UPDATE it instead of creating a duplicate
        const draftId = autoSavedDraftIdRef.current;
        campaignPack.pack_id = draftId;
        campaignPack.updated_by = userIdentifier;
        campaignPack.change_summary = saveAsDraft ? 'Saved as draft' : 'Promoted from draft to published';

        await axios.put(`${API_BASE}/api/v3/packs/${draftId}`, campaignPack, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess(saveAsDraft
          ? '💾 Draft saved!'
          : '✅ Draft published!');
      } else {
        // Create new — backend generates pack_id; capture it from response
        const resp = await axios.post(`${API_BASE}/api/v3/packs`, campaignPack, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const serverPackId = resp.data?.pack?.pack_id || resp.data?.pack_id;
        if (serverPackId) {
          autoSavedDraftIdRef.current = serverPackId;
          setAutoSavedDraftId(serverPackId);
        }
        setSuccess(saveAsDraft
          ? '💾 Campaign Direction created as draft!'
          : '✅ Campaign Direction created and published!');
      }

      // Reset form and reload
      setTimeout(() => {
        setShowForm(false);
        setEditingCampaign(null);
        setSuccess(null);
        loadCampaigns();
      }, 2000);

    } catch (error) {
      console.error('Error saving campaign:', error);
      // Extract a useful message from FastAPI's 422 validation envelope OR
      // a plain detail string. Avoids the previous generic "Request failed".
      const data = error.response?.data;
      let msg = data?.detail;
      if (!msg && Array.isArray(data?.errors) && data.errors.length) {
        const first = data.errors[0];
        const field = Array.isArray(first.loc) ? first.loc.join('.') : (first.loc || 'field');
        msg = `${data.message || 'Validation failed'} — ${field}: ${first.msg || 'invalid'}`;
      }
      if (!msg) msg = data?.message || error.message || 'Failed to save campaign direction';
      setError(msg);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------------------------------
  // AUTO-SAVE DRAFT (every 10s while form is open)
  // ----------------------------------------------------------------
  // Lightweight, silent equivalent of "Save as Draft":
  //   • Skips when form is closed, currently submitting, or required
  //     fields are not yet filled (avoids spamming invalid drafts).
  //   • First successful auto-save remembers the draft pack_id, then
  //     subsequent ticks UPDATE that same draft instead of creating
  //     new ones.
  //   • Shows a tiny indicator next to the action buttons.
  const autoSaveDraft = async () => {
    if (!showForm || loading || autoSaveStatus === 'saving') return;
    // Don't auto-save when EDITING an existing PUBLISHED pack — those
    // have their own versioning flow on Update & Publish.
    if (editingCampaign && (editingCampaign.metadata?.status || editingCampaign.status) === 'published') return;
    // Minimum quality bar: BM name + at least one why_trust reason in BM
    if (!formData.campaign_name_ms.trim()) return;
    if (!formData.why_trust[0]?.reason_ms?.trim()) return;

    const token = secureStorage.getItem('nexus_access_token');
    if (!token) return;
    const userIdentifier = user?.email || user?.user_id || 'management';

    const targetPackId =
      autoSavedDraftIdRef.current ||
      editingCampaign?.pack_id ||
      null;
    const isUpdate = !!targetPackId;

    const campaignPack = {
      pack_type: 'direction',
      created_by: userIdentifier,
      updated_by: userIdentifier,
      change_summary: 'Auto-saved draft',
      metadata_overrides: {
        status: 'draft',
        visibility: 'public',
        tags: ['campaign-direction', 'strategic', 'auto-saved'],
      },
      name: { ms: formData.campaign_name_ms, en: formData.campaign_name_en },
      description: {
        ms: formData.solution_positioning_ms || 'Strategic campaign direction',
        en: formData.solution_positioning_en || 'Strategic campaign direction',
      },
      content: {
        campaign_name: { ms: formData.campaign_name_ms, en: formData.campaign_name_en },
        direction_level: 'product',
        objective: formData.objective,
        why_trust: formData.why_trust.map((t) => ({
          reason: { ms: t.reason_ms, en: t.reason_en },
          research_source: t.research_source,
          priority: t.priority,
        })),
        why_buy: formData.why_buy.map((b) => ({
          reason: { ms: b.reason_ms, en: b.reason_en },
          research_source: b.research_source,
          priority: b.priority,
        })),
        customer_pain_insights: formData.customer_pain_insights.filter((p) => p.ms || p.en),
        solution_positioning: { ms: formData.solution_positioning_ms, en: formData.solution_positioning_en },
        key_message: { ms: formData.key_message_ms, en: formData.key_message_en },
        applicable_products: formData.applicable_products,
        priority_level: formData.priority_level,
        tone: formData.tone,
      },
      effective_from: formData.active_from,
      effective_until: formData.active_until || null,
    };

    try {
      setAutoSaveStatus('saving');
      if (isUpdate) {
        await axios.put(`${API_BASE}/api/v3/packs/${targetPackId}`, campaignPack, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        // Backend ignores client-supplied pack_id and generates its own —
        // we MUST capture it from the response so subsequent ticks PUT to
        // the correct id.
        const resp = await axios.post(`${API_BASE}/api/v3/packs`, campaignPack, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const serverPackId = resp.data?.pack?.pack_id || resp.data?.pack_id;
        if (serverPackId) {
          autoSavedDraftIdRef.current = serverPackId;
          setAutoSavedDraftId(serverPackId);
        }
      }
      setLastAutoSavedAt(new Date());
      setAutoSaveStatus('saved');
    } catch (err) {
      console.warn('[AutoSave] failed (silent):', err?.response?.data || err?.message);
      setAutoSaveStatus('error');
    }
  };

  // 10-second tick — restarts whenever form opens / formData changes
  useEffect(() => {
    if (!showForm) {
      // Reset auto-save state when the form is closed/cancelled so the next
      // "New Campaign" doesn't accidentally update the previous draft.
      setAutoSaveStatus('idle');
      setLastAutoSavedAt(null);
      setAutoSavedDraftId(null);
      autoSavedDraftIdRef.current = null;
      return undefined;
    }
    const id = setInterval(autoSaveDraft, 10000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [showForm, formData, loading, editingCampaign]);

  const incrementVersion = (version) => {
    const parts = version.split('.');
    parts[1] = parseInt(parts[1]) + 1;
    return parts.join('.');
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setError(null);
    setSuccess(null);
    
    const content = campaign.content || {};
    
    setFormData({
      campaign_name_ms: campaign.name?.ms || '',
      campaign_name_en: campaign.name?.en || '',
      objective: content.objective || 'sales',
      why_trust: (content.why_trust || []).map(t => ({
        reason_ms: t.reason?.ms || '',
        reason_en: t.reason?.en || '',
        research_source: t.research_source || '',
        priority: t.priority || 1
      })),
      why_buy: (content.why_buy || []).map(b => ({
        reason_ms: b.reason?.ms || '',
        reason_en: b.reason?.en || '',
        research_source: b.research_source || '',
        priority: b.priority || 1
      })),
      customer_pain_insights: content.customer_pain_insights || [{ ms: '', en: '' }],
      solution_positioning_ms: content.solution_positioning?.ms || '',
      solution_positioning_en: content.solution_positioning?.en || '',
      key_message_ms: content.key_message?.ms || '',
      key_message_en: content.key_message?.en || '',
      applicable_products: content.applicable_products || [],
      priority_level: content.priority_level || 1,
      active_from: campaign.effective_from || new Date().toISOString().split('T')[0],
      active_until: campaign.effective_until || '',
      tone: content.tone || 'Professional'
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (campaignId, campaignName) => {
    if (!window.confirm(`Are you sure you want to delete "${campaignName}"?`)) {
      return;
    }

    try {
      setLoading(true);
      const token = secureStorage.getItem('nexus_access_token');
      await axios.delete(`${API_BASE}/api/v3/packs/${campaignId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('✅ Campaign deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      loadCampaigns();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      setError(error.response?.data?.detail || 'Failed to delete campaign');
      setTimeout(() => setError(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Styles
  const styles = {
    container: {
      padding: EMZI_SPACING.md,
      maxWidth: '800px',
      margin: '0 auto',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      paddingBottom: '100px' // Space for mobile navigation
    },
    header: {
      background: `linear-gradient(135deg, ${EMZI_COLORS.primary} 0%, ${EMZI_COLORS.secondary} 100%)`,
      color: '#fff',
      padding: EMZI_SPACING.lg,
      borderRadius: EMZI_RADIUS.lg,
      marginBottom: EMZI_SPACING.lg,
      boxShadow: EMZI_SHADOWS.md
    },
    backButton: {
      padding: '10px 16px',
      border: 'none',
      borderRadius: EMZI_RADIUS.md,
      background: 'rgba(255, 255, 255, 0.2)',
      color: '#fff',
      cursor: 'pointer',
      fontSize: '14px',
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    title: {
      fontSize: '24px',
      fontWeight: '700',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    subtitle: {
      fontSize: '14px',
      opacity: 0.9
    },
    langToggle: {
      display: 'flex',
      gap: '8px',
      marginTop: '12px'
    },
    langButton: (active) => ({
      padding: '6px 16px',
      border: 'none',
      borderRadius: EMZI_RADIUS.sm,
      background: active ? '#fff' : 'rgba(255, 255, 255, 0.2)',
      color: active ? EMZI_COLORS.primary : '#fff',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: active ? '600' : '400',
      transition: 'all 0.2s'
    }),
    alert: (type) => ({
      padding: EMZI_SPACING.md,
      borderRadius: EMZI_RADIUS.md,
      marginBottom: EMZI_SPACING.md,
      background: type === 'error' ? '#fee2e2' : '#d1fae5',
      color: type === 'error' ? '#991b1b' : '#065f46',
      border: `1px solid ${type === 'error' ? '#fecaca' : '#a7f3d0'}`,
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: '14px',
      fontWeight: '500'
    }),
    createButton: {
      width: '100%',
      padding: EMZI_SPACING.md,
      background: EMZI_COLORS.primary,
      color: '#fff',
      border: 'none',
      borderRadius: EMZI_RADIUS.lg,
      fontSize: '16px',
      fontWeight: '700',
      cursor: 'pointer',
      marginBottom: EMZI_SPACING.lg,
      boxShadow: EMZI_SHADOWS.sm,
      transition: 'transform 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px'
    },
    form: {
      background: '#fff',
      padding: EMZI_SPACING.lg,
      borderRadius: EMZI_RADIUS.lg,
      marginBottom: EMZI_SPACING.lg,
      boxShadow: EMZI_SHADOWS.md
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '700',
      marginBottom: EMZI_SPACING.md,
      color: EMZI_COLORS.textPrimary,
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    sectionCard: {
      background: '#f9fafb',
      padding: EMZI_SPACING.md,
      borderRadius: EMZI_RADIUS.md,
      marginBottom: EMZI_SPACING.lg,
      border: '1px solid #e5e7eb'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontSize: '14px',
      fontWeight: '600',
      color: EMZI_COLORS.textPrimary
    },
    input: {
      width: '100%',
      padding: '12px',
      marginBottom: '12px',
      borderRadius: EMZI_RADIUS.md,
      border: '1px solid #d1d5db',
      fontSize: '16px',
      fontFamily: 'inherit',
      transition: 'border 0.2s',
      WebkitAppearance: 'none', // Fix for iOS
      boxSizing: 'border-box'
    },
    textarea: {
      width: '100%',
      padding: '12px',
      marginBottom: '12px',
      borderRadius: EMZI_RADIUS.md,
      border: '1px solid #d1d5db',
      fontSize: '14px',
      fontFamily: 'inherit',
      resize: 'vertical',
      minHeight: '80px',
      boxSizing: 'border-box'
    },
    select: {
      width: '100%',
      padding: '12px',
      marginBottom: '12px',
      borderRadius: EMZI_RADIUS.md,
      border: '1px solid #d1d5db',
      fontSize: '16px',
      fontFamily: 'inherit',
      background: '#fff',
      cursor: 'pointer',
      WebkitAppearance: 'none',
      boxSizing: 'border-box'
    },
    reasonCard: {
      background: '#fff',
      padding: EMZI_SPACING.md,
      borderRadius: EMZI_RADIUS.md,
      marginBottom: EMZI_SPACING.sm,
      border: '1px solid #e5e7eb',
      boxShadow: EMZI_SHADOWS.xs
    },
    addButton: {
      width: '100%',
      padding: '12px',
      background: EMZI_COLORS.success,
      color: '#fff',
      border: 'none',
      borderRadius: EMZI_RADIUS.md,
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    removeButton: {
      padding: '6px 12px',
      background: EMZI_COLORS.danger,
      color: '#fff',
      border: 'none',
      borderRadius: EMZI_RADIUS.sm,
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: '600'
    },
    actionButtons: {
      display: 'flex',
      gap: '12px',
      marginTop: EMZI_SPACING.lg
    },
    cancelButton: {
      flex: 1,
      padding: '14px',
      background: '#fff',
      border: '2px solid #d1d5db',
      borderRadius: EMZI_RADIUS.md,
      cursor: 'pointer',
      fontSize: '15px',
      fontWeight: '600',
      color: EMZI_COLORS.textSecondary
    },
    submitButton: (disabled) => ({
      flex: 2,
      padding: '14px',
      background: disabled ? '#9ca3af' : EMZI_COLORS.primary,
      color: '#fff',
      border: 'none',
      borderRadius: EMZI_RADIUS.md,
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: '15px',
      fontWeight: '700',
      boxShadow: disabled ? 'none' : EMZI_SHADOWS.sm
    }),
    campaignCard: {
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: EMZI_RADIUS.lg,
      padding: EMZI_SPACING.md,
      marginBottom: EMZI_SPACING.md,
      boxShadow: EMZI_SHADOWS.sm,
      transition: 'transform 0.2s, box-shadow 0.2s'
    },
    emptyState: {
      background: '#f9fafb',
      padding: '60px 20px',
      borderRadius: EMZI_RADIUS.lg,
      textAlign: 'center',
      border: '2px dashed #d1d5db'
    },
    productCheckbox: {
      display: 'flex',
      alignItems: 'center',
      padding: '12px',
      borderBottom: '1px solid #f0f0f0',
      cursor: 'pointer',
      transition: 'background 0.2s'
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button
          onClick={() => navigate('/knowledge-packs')}
          style={styles.backButton}
        >
          <span>←</span> Back to Knowledge Packs
        </button>
        
        <h1 style={styles.title}>
          <span>🎯</span>
          Campaign Direction
        </h1>
        <p style={styles.subtitle}>
          {lang === 'ms' 
            ? 'Urus strategi kempen berdasarkan kajian pasaran'
            : 'Manage strategic campaign direction based on market research'
          }
        </p>

        {/* Language Toggle */}
        <div style={styles.langToggle}>
          <button
            onClick={() => setLang('ms')}
            style={styles.langButton(lang === 'ms')}
          >
            BM
          </button>
          <button
            onClick={() => setLang('en')}
            style={styles.langButton(lang === 'en')}
          >
            EN
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div style={styles.alert('error')}>
          <span>⚠️</span>
          {error}
        </div>
      )}

      {success && (
        <div style={styles.alert('success')}>
          <span>✅</span>
          {success}
        </div>
      )}

      {/* Create Button */}
      {!showForm && (
        <button
          onClick={handleCreate}
          style={styles.createButton}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <span>+</span>
          {lang === 'ms' ? 'Cipta Kempen Baharu' : 'Create New Campaign'}
        </button>
      )}

      {/* Campaign Form */}
      {showForm && (
        <form onSubmit={handleSubmit} style={styles.form}>
          <h2 style={styles.sectionTitle}>
            {editingCampaign 
              ? (lang === 'ms' ? '✏️ Edit Kempen' : '✏️ Edit Campaign')
              : (lang === 'ms' ? '➕ Kempen Baharu' : '➕ New Campaign')
            }
          </h2>

          {/* Section 1: Identity */}
          <div style={styles.sectionCard}>
            <h3 style={{ ...styles.sectionTitle, fontSize: '16px' }}>
              1️⃣ {lang === 'ms' ? 'Identiti Kempen' : 'Campaign Identity'}
            </h3>
            
            <label style={styles.label}>
              {lang === 'ms' ? 'Nama Kempen (BM)' : 'Campaign Name (BM)'} *
            </label>
            <input
              type="text"
              value={formData.campaign_name_ms}
              onChange={(e) => setFormData({ ...formData, campaign_name_ms: e.target.value })}
              required
              placeholder="Contoh: Resepi Emas Ustaz Kazim"
              style={styles.input}
            />

            <label style={styles.label}>
              {lang === 'ms' ? 'Nama Kempen (EN)' : 'Campaign Name (EN)'} *
            </label>
            <input
              type="text"
              value={formData.campaign_name_en}
              onChange={(e) => setFormData({ ...formData, campaign_name_en: e.target.value })}
              required
              placeholder="Example: Ustaz Kazim's Golden Recipe"
              style={styles.input}
            />

            <label style={styles.label}>
              {lang === 'ms' ? 'Objektif' : 'Objective'} *
            </label>
            <select
              value={formData.objective}
              onChange={(e) => setFormData({ ...formData, objective: e.target.value })}
              style={styles.select}
            >
              <option value="sales">{lang === 'ms' ? 'Jualan' : 'Sales'}</option>
              <option value="awareness">{lang === 'ms' ? 'Kesedaran' : 'Awareness'}</option>
              <option value="engagement">{lang === 'ms' ? 'Penglibatan' : 'Engagement'}</option>
              <option value="education">{lang === 'ms' ? 'Pendidikan' : 'Education'}</option>
            </select>
          </div>

          {/* Section 2: WHY TRUST */}
          <div style={styles.sectionCard}>
            <h3 style={{ ...styles.sectionTitle, fontSize: '16px' }}>
              2️⃣ WHY TRUST {lang === 'ms' ? '(Dari Kajian)' : '(From Research)'} *
            </h3>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
              {lang === 'ms' 
                ? 'Apa yang buat pelanggan percaya produk ini? (Berdasarkan survei/kajian)'
                : 'What makes customers trust this product? (Based on surveys/research)'
              }
            </p>
            
            {formData.why_trust.map((trust, index) => (
              <div key={`item-${index}`} style={styles.reasonCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <strong style={{ fontSize: '14px' }}>
                    {lang === 'ms' ? 'Sebab Percaya' : 'Trust Reason'} #{index + 1}
                  </strong>
                  {formData.why_trust.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveTrustReason(index)}
                      style={styles.removeButton}
                    >
                      🗑️ {lang === 'ms' ? 'Padam' : 'Remove'}
                    </button>
                  )}
                </div>
                
                <input
                  type="text"
                  placeholder={lang === 'ms' ? 'Sebab (BM): Contoh - Resepi Ustaz Kazim' : 'Reason (BM): Example - Ustaz Kazim\'s Recipe'}
                  value={trust.reason_ms}
                  onChange={(e) => handleTrustReasonChange(index, 'reason_ms', e.target.value)}
                  required
                  style={styles.input}
                />
                
                <input
                  type="text"
                  placeholder={lang === 'ms' ? 'Sebab (EN): Example - Ustaz Kazim\'s Recipe' : 'Reason (EN): Example - Ustaz Kazim\'s Recipe'}
                  value={trust.reason_en}
                  onChange={(e) => handleTrustReasonChange(index, 'reason_en', e.target.value)}
                  required
                  style={styles.input}
                />
                
                <input
                  type="text"
                  placeholder={lang === 'ms' ? 'Sumber Kajian: Contoh - Q2 2024 Survey (n=500)' : 'Research Source: Example - Q2 2024 Survey (n=500)'}
                  value={trust.research_source}
                  onChange={(e) => handleTrustReasonChange(index, 'research_source', e.target.value)}
                  style={styles.input}
                />
                
                <label style={{ fontSize: '13px' }}>
                  {lang === 'ms' ? 'Keutamaan' : 'Priority'}: 
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={trust.priority}
                  onChange={(e) => handleTrustReasonChange(index, 'priority', parseInt(e.target.value))}
                  style={{ width: '60px', padding: '6px', borderRadius: EMZI_RADIUS.sm, border: '1px solid #d1d5db', marginLeft: '8px' }}
                />
              </div>
            ))}
            
            <button
              type="button"
              onClick={handleAddTrustReason}
              style={styles.addButton}
            >
              <span>+</span>
              {lang === 'ms' ? 'Tambah Sebab Percaya' : 'Add Trust Reason'}
            </button>
          </div>

          {/* Section 3: WHY BUY */}
          <div style={styles.sectionCard}>
            <h3 style={{ ...styles.sectionTitle, fontSize: '16px' }}>
              3️⃣ WHY BUY {lang === 'ms' ? '(Pencetus Pembelian)' : '(Purchase Triggers)'} *
            </h3>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
              {lang === 'ms' 
                ? 'Apa yang mencetus keputusan beli? (Berdasarkan kajian)'
                : 'What triggers purchase decision? (Based on research)'
              }
            </p>
            
            {formData.why_buy.map((buy, index) => (
              <div key={`item-${index}`} style={styles.reasonCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <strong style={{ fontSize: '14px' }}>
                    {lang === 'ms' ? 'Pencetus' : 'Buy Trigger'} #{index + 1}
                  </strong>
                  {formData.why_buy.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveBuyReason(index)}
                      style={styles.removeButton}
                    >
                      🗑️ {lang === 'ms' ? 'Padam' : 'Remove'}
                    </button>
                  )}
                </div>
                
                <input
                  type="text"
                  placeholder={lang === 'ms' ? 'Pencetus (BM): Contoh - Jaminan 30 hari' : 'Trigger (BM): Example - 30-day guarantee'}
                  value={buy.reason_ms}
                  onChange={(e) => handleBuyReasonChange(index, 'reason_ms', e.target.value)}
                  required
                  style={styles.input}
                />
                
                <input
                  type="text"
                  placeholder={lang === 'ms' ? 'Pencetus (EN): Example - 30-day guarantee' : 'Trigger (EN): Example - 30-day guarantee'}
                  value={buy.reason_en}
                  onChange={(e) => handleBuyReasonChange(index, 'reason_en', e.target.value)}
                  required
                  style={styles.input}
                />
                
                <input
                  type="text"
                  placeholder={lang === 'ms' ? 'Sumber Kajian' : 'Research Source'}
                  value={buy.research_source}
                  onChange={(e) => handleBuyReasonChange(index, 'research_source', e.target.value)}
                  style={styles.input}
                />
              </div>
            ))}
            
            <button
              type="button"
              onClick={handleAddBuyReason}
              style={styles.addButton}
            >
              <span>+</span>
              {lang === 'ms' ? 'Tambah Pencetus Beli' : 'Add Buy Trigger'}
            </button>
          </div>

          {/* Section 4: Solution Positioning */}
          <div style={styles.sectionCard}>
            <h3 style={{ ...styles.sectionTitle, fontSize: '16px' }}>
              4️⃣ {lang === 'ms' ? 'Positioning Penyelesaian' : 'Solution Positioning'}
            </h3>
            
            <label style={styles.label}>
              {lang === 'ms' ? 'Positioning (BM)' : 'Positioning (BM)'}
            </label>
            <textarea
              value={formData.solution_positioning_ms}
              onChange={(e) => setFormData({ ...formData, solution_positioning_ms: e.target.value })}
              placeholder={lang === 'ms' ? 'Contoh: Penyelesaian semula jadi untuk diabetes yang selamat dan berkesan' : 'Example: Safe and effective natural solution for diabetes'}
              style={styles.textarea}
            />
            
            <label style={styles.label}>
              {lang === 'ms' ? 'Positioning (EN)' : 'Positioning (EN)'}
            </label>
            <textarea
              value={formData.solution_positioning_en}
              onChange={(e) => setFormData({ ...formData, solution_positioning_en: e.target.value })}
              placeholder="Example: Safe and effective natural solution for diabetes"
              style={styles.textarea}
            />
          </div>

          {/* Section 5: Apply to Products */}
          <div style={styles.sectionCard}>
            <h3 style={{ ...styles.sectionTitle, fontSize: '16px' }}>
              5️⃣ {lang === 'ms' ? 'Gunakan Untuk Produk' : 'Apply to Products'} *
            </h3>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
              {lang === 'ms' 
                ? 'Pilih produk yang guna campaign direction ini'
                : 'Select which products use this campaign direction'
              }
            </p>
            
            <div style={{ background: '#fff', padding: '16px', borderRadius: EMZI_RADIUS.md, border: '1px solid #e5e7eb', maxHeight: '300px', overflowY: 'auto' }}>
              {products.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                  {lang === 'ms' ? 'Tiada produk. Cipta produk dahulu.' : 'No products available. Create products first.'}
                </p>
              ) : (
                products.map(product => (
                  <label key={product.pack_id} style={styles.productCheckbox}>
                    <input
                      type="checkbox"
                      checked={formData.applicable_products.includes(product.pack_id)}
                      onChange={() => handleProductToggle(product.pack_id)}
                      style={{ width: '20px', height: '20px', marginRight: '12px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '15px', fontWeight: formData.applicable_products.includes(product.pack_id) ? '600' : '400' }}>
                      {product.name?.ms || product.name?.en || product.pack_id}
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Section 6: Active Period */}
          <div style={styles.sectionCard}>
            <h3 style={{ ...styles.sectionTitle, fontSize: '16px' }}>
              6️⃣ {lang === 'ms' ? 'Tempoh Aktif' : 'Active Period'}
            </h3>
            
            <label style={styles.label}>
              {lang === 'ms' ? 'Aktif Dari' : 'Active From'} *
            </label>
            <input
              type="date"
              value={formData.active_from}
              onChange={(e) => setFormData({ ...formData, active_from: e.target.value })}
              required
              style={styles.input}
            />
            
            <label style={styles.label}>
              {lang === 'ms' ? 'Aktif Hingga (Pilihan)' : 'Active Until (Optional)'}
            </label>
            <input
              type="date"
              value={formData.active_until}
              onChange={(e) => setFormData({ ...formData, active_until: e.target.value })}
              style={styles.input}
            />
          </div>

          {/* Auto-save indicator */}
          <div
            data-testid="autosave-indicator"
            style={{
              padding: '6px 10px',
              fontSize: '11px',
              color: '#666',
              textAlign: 'center',
              minHeight: '22px',
              fontStyle: 'italic',
              marginTop: '8px',
            }}
          >
            {autoSaveStatus === 'saving' && (
              <span style={{ color: '#0e67a2' }}>💾 {lang === 'ms' ? 'Sedang menyimpan draf...' : 'Auto-saving draft...'}</span>
            )}
            {autoSaveStatus === 'saved' && lastAutoSavedAt && (
              <span style={{ color: '#2E7D32' }}>
                ✓ {lang === 'ms' ? 'Draf disimpan automatik' : 'Draft auto-saved'} {lastAutoSavedAt.toLocaleTimeString()}
              </span>
            )}
            {autoSaveStatus === 'error' && (
              <span style={{ color: '#C62828' }}>⚠️ {lang === 'ms' ? 'Auto-save gagal — sila simpan secara manual' : 'Auto-save failed — please save manually'}</span>
            )}
            {autoSaveStatus === 'idle' && (
              <span style={{ opacity: 0.6 }}>{lang === 'ms' ? 'Auto-save aktif (setiap 10s)' : 'Auto-save active (every 10s)'}</span>
            )}
          </div>

          {/* Action Buttons */}
          <div style={styles.actionButtons}>
            <button
              type="button"
              data-testid="cancel-campaign-btn"
              onClick={() => {
                setShowForm(false);
                setEditingCampaign(null);
                setError(null);
              }}
              style={styles.cancelButton}
            >
              {lang === 'ms' ? 'Batal' : 'Cancel'}
            </button>
            <button
              type="button"
              data-testid="save-draft-campaign-btn"
              disabled={loading}
              onClick={(e) => handleSubmit(e, true)}
              style={{
                ...styles.submitButton(loading),
                background: '#fff',
                color: '#0e67a2',
                border: '2px solid #0e67a2',
              }}
            >
              {loading
                ? (lang === 'ms' ? 'Menyimpan...' : 'Saving...')
                : (lang === 'ms' ? '💾 Simpan sebagai Draf' : '💾 Save as Draft')}
            </button>
            <button
              type="submit"
              data-testid="publish-campaign-btn"
              disabled={loading}
              style={styles.submitButton(loading)}
            >
              {loading
                ? (lang === 'ms' ? 'Menerbitkan...' : 'Publishing...')
                : (editingCampaign
                  ? (lang === 'ms' ? '✅ Kemaskini & Terbit' : '✅ Update & Publish')
                  : (lang === 'ms' ? '✅ Cipta & Terbit' : '✅ Create & Publish')
                )
              }
            </button>
          </div>
        </form>
      )}

      {/* Campaign List */}
      {!showForm && (
        <div>
          <h2 style={{ ...styles.sectionTitle, marginBottom: EMZI_SPACING.md }}>
            📋 {lang === 'ms' ? 'Kempen Sedia Ada' : 'Existing Campaigns'}
          </h2>
          
          {loading && campaigns.length === 0 ? (
            <div style={styles.emptyState}>
              <p style={{ fontSize: '16px', color: '#6b7280' }}>
                {lang === 'ms' ? 'Memuatkan...' : 'Loading...'}
              </p>
            </div>
          ) : campaigns.length === 0 ? (
            <div style={styles.emptyState}>
              <p style={{ fontSize: '48px', marginBottom: '16px' }}>📭</p>
              <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '8px', fontWeight: '600' }}>
                {lang === 'ms' ? 'Tiada Kempen Lagi' : 'No Campaigns Yet'}
              </p>
              <p style={{ fontSize: '14px', color: '#9ca3af' }}>
                {lang === 'ms' ? 'Cipta kempen pertama di atas' : 'Create your first campaign above'}
              </p>
            </div>
          ) : (
            campaigns.map(campaign => (
              <div 
                key={campaign.pack_id} 
                style={styles.campaignCard}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = EMZI_SHADOWS.md;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = EMZI_SHADOWS.sm;
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '4px', color: EMZI_COLORS.primary }}>
                      {lang === 'ms' 
                        ? (campaign.name?.ms || campaign.name?.en || 'Untitled Campaign')
                        : (campaign.name?.en || campaign.name?.ms || 'Untitled Campaign')
                      }
                    </h3>
                    <p style={{ fontSize: '12px', color: '#6b7280' }}>
                      {lang === 'ms' ? 'Versi' : 'Version'}: {campaign.version}
                      {' | '}
                      <span
                        data-testid={`campaign-status-${campaign.pack_id}`}
                        style={{
                          padding: '2px 8px',
                          borderRadius: '10px',
                          fontWeight: 700,
                          fontSize: '10px',
                          background: (campaign.metadata?.status || campaign.status) === 'published' ? '#E8F5E9' : '#FFF8E1',
                          color: (campaign.metadata?.status || campaign.status) === 'published' ? '#1B5E20' : '#F57F17',
                        }}
                      >
                        {(campaign.metadata?.status || campaign.status || 'draft').toUpperCase()}
                      </span>
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleEdit(campaign)}
                      style={{ 
                        padding: '8px 16px', 
                        background: EMZI_COLORS.warning, 
                        color: '#fff', 
                        border: 'none', 
                        borderRadius: EMZI_RADIUS.sm, 
                        cursor: 'pointer', 
                        fontSize: '13px', 
                        fontWeight: '600' 
                      }}
                    >
                      ✏️ {lang === 'ms' ? 'Edit' : 'Edit'}
                    </button>
                    <button
                      onClick={() => handleDelete(campaign.pack_id, campaign.name?.ms || campaign.name?.en)}
                      style={{ 
                        padding: '8px 16px', 
                        background: EMZI_COLORS.danger, 
                        color: '#fff', 
                        border: 'none', 
                        borderRadius: EMZI_RADIUS.sm, 
                        cursor: 'pointer', 
                        fontSize: '13px', 
                        fontWeight: '600' 
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', padding: '12px', background: '#f9fafb', borderRadius: EMZI_RADIUS.sm }}>
                  <div>
                    <p style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>
                      {lang === 'ms' ? 'Sebab Percaya' : 'Why Trust'}
                    </p>
                    <p style={{ fontSize: '16px', fontWeight: '700', color: EMZI_COLORS.primary }}>
                      {(campaign.content?.why_trust || []).length}
                    </p>
                  </div>
                  <div>
                    <p style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>
                      {lang === 'ms' ? 'Pencetus Beli' : 'Why Buy'}
                    </p>
                    <p style={{ fontSize: '16px', fontWeight: '700', color: EMZI_COLORS.success }}>
                      {(campaign.content?.why_buy || []).length}
                    </p>
                  </div>
                  <div>
                    <p style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>
                      {lang === 'ms' ? 'Produk' : 'Products'}
                    </p>
                    <p style={{ fontSize: '16px', fontWeight: '700', color: EMZI_COLORS.secondary }}>
                      {(campaign.content?.applicable_products || []).length}
                    </p>
                  </div>
                  <div>
                    <p style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>
                      {lang === 'ms' ? 'Objektif' : 'Objective'}
                    </p>
                    <p style={{ fontSize: '13px', fontWeight: '600', color: EMZI_COLORS.textPrimary }}>
                      {campaign.content?.objective || 'sales'}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default CampaignDirectionManager;
